<template>
  <div
    id="analysis-recos-content"
    class="detail-summay-containter"
  >
    <div>
      <table>
        <thead>
          <tr>
            <th>{{ $t('vendors') }}</th>
            <th class="align-right">
              {{ $t('summary.submittedAmount') }}
            </th>
            <th class="align-right">
              {{ $t('summary.diffToLowestBid', [currencyCode]) }}
            </th>
            <th class="align-right">
              {{ $t('summary.pctToLowestBid') }}
            </th>
            <th class="align-right">
              {{ $t('summary.finalAmount') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="bid in bids"
            :key="bid.id"
            :class="[{'highlight-recommended': highlightSelectedBid(bid) },
                     {lowestLine: highlightLowestBid(bid)}]"
          >
            <td class="vendor-column">
              {{ deep(bid, 'company.name') || '' }}
            </td>
            <td class="open-sum align-right">
              <div>{{ renderedStatus(bid) }}</div>
              <span v-if="bid.content">{{ bid.content.declineReason }}</span>
            </td>
            <td class="align-right">
              {{ showDiffToLowestAmount(bid) }}
            </td>
            <td class="align-right">
              {{ showPctToLowestSum(bid) }}
            </td>
            <td class="final-amount align-right">
              <span
                v-if="showAdjustedInput(bid)"
              >
                {{ currencyCode }}
              </span><input
                v-if="canEdit && isEditing && showAdjustedInput(bid)"
                v-autowrap="{offset: 19}"
                v-fusioninputcheck="{
                  type: 'decimal',
                  pattern: (val) => (Number((val || '').replace(/,/g, '')) || 0) > 0,
                  required: !!bid.content.service.totalFeeAdjustedText
                }"
                class="input"
                type="text"
                :value="bid.content.service.totalFeeAdjustedText"
                @change="event => markUpdate(bid, event.target.value)"
              >
              <input
                v-if="isReadOnly && showAdjustedInput(bid)"
                v-autowrap="{offset: 19}"
                class="input"
                type="text"
                :value="CurrencyFormatter.format(bid.content.service.totalFeeAdjustedText || bid.content.service.totalFee, 'USD')"
                :disabled="isReadOnly"
                @change="event => markUpdate(bid, event.target.value)"
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Sticky
      v-if="canEdit || isAwarded || isReview"
      id="analysis-reco-nav-bar-sticky"
      class="sticky"
    >
      <div
        id="analysis-reco-nav-bar"
        class="nav-bar clearfix"
      >
        <div
          v-if="canEdit && isPicView"
          class="clearfix nav-bar-item"
        >
          <AwardTender
            :clickCheck="editableCheck"
            :isUnderEditing="!editable"
            :recommendedBidId="selectedBidId"
            :saveAnalysisRecoData="save"
          />
        </div>
        <button
          v-if="canEdit && isEditing"
          type="button"
          class="btn btn--orange"
          @click="save()"
        >{{ $t('action.save') }}</button>
        <button
          v-if="canEdit && isEditing"
          type="button"
          class="btn btn--darkblue"
          @click="cancelEdit()"
        >{{ $t('action.cancel') }}</button>
        <button
          type="button"
          class="btn btn--darkblue"
          @click="openAnalysis()"
        >{{ $t('summary.runAnalysis') }}</button>
        <button
          type="button"
          class="btn btn--darkblue"
          @click="exportPDF()"
        >{{ $t('action.generatePDF') }}</button>
        <button
          v-if="canEdit && !isEditing"
          v-note="{text:noteText, enable:!editable}"
          type="button"
          class="btn btn--darkblue"
          :disabled="!editable"
          @click="enableEdit()"
        >{{ $t('action.edit') }}</button>
      </div>
    </Sticky>
    <div class="recommendation-container clearfix">
      <Collapse
        :title="$t('summary.recommendation')"
        :withBorder="false"
      >
        <template slot="content">
          <div id="accordion-recommendation-content">
            <div id="detail-list">
              <div
                v-if="showQuerySummary"
                class="summary-detail-tender-query"
              >
                <div class="title">
                  {{ $t('summary.tenderQuerySummary') }}:
                </div>
                <div class="description">
                  <pre
                    v-if="isReadOnly"
                    class="pre-text"
                  >{{ analysisReco.data.summary }}</pre>
                  <textarea
                    v-else
                    id="tender-query-summary-textarea"
                    v-autosize
                    v-hkldinput
                    :disabled="isReadOnly"
                    class="autosize"
                    placeholder="Please input your remarks"
                    type="text"
                    :value="analysisReco.data.summary"
                    @change="event => updateAnalysisData('summary', event.target.value)"
                  />
                </div>
              </div>
              <div id="summary-detail-tender-analysis">
                <div class="title">
                  {{ $t('summary.tenderAnalysis') }}:
                </div>
                <div class="description">
                  <pre
                    v-if="isReadOnly"
                    class="pre-text"
                  >{{ analysisReco.data.analysis }}</pre>
                  <textarea
                    v-else
                    id="tender-analysis-textarea"
                    v-autosize
                    v-checkVisibility="{refresh: true}"
                    v-hkldinput
                    :disabled="isReadOnly"
                    class="autosize"
                    placeholder="Please input your remarks"
                    type="text"
                    :value="analysisReco.data.analysis"
                    @change="event => updateAnalysisData('analysis', event.target.value)"
                  />
                </div>
              </div>
              <div>
                <div class="title">
                  {{ $t('summary.recommendation') }}:
                </div>
                <div class="description">
                  <pre
                    v-if="isReadOnly"
                    class="pre-text"
                  >{{ analysisReco.data.recommendation }}</pre>
                  <textarea
                    v-else
                    id="recommendation-textarea"
                    v-autosize
                    v-checkVisibility="{refresh: true}"
                    v-hkldinput
                    :disabled="isReadOnly"
                    class="autosize"
                    placeholder="Please input your remarks"
                    type="text"
                    :value="analysisReco.data.recommendation"
                    @change="event => updateAnalysisData('recommendation', event.target.value)"
                  />
                </div>
              </div>

              <div class="row recommend-summary">
                <div class="small-5 medium-5 large-5 columns">
                  <div class="title">
                    {{ $t('summary.recommendedVendor') }}:
                  </div>
                  <div id="recommend-vendor">
                    <HKLSelect
                      :value="selectedBidVendorName || undefined"
                      :options="recommendedVendorHklOptions"
                      :disabled="isReadOnly"
                      :showSearch="false"
                      placeholder=""
                      @change="setRecommendedBidId"
                    />
                  </div>
                </div>
                <div class="small-4 medium-4 large-4 columns">
                  <label class="title align-right">{{ $t('summary.recommendedAmount') }}: </label>
                  <label class="currency-amount align-right">
                    {{ recommendedAmount }}
                  </label>
                </div>
                <div class="small-3 medium-3 large-3">
                  <label class="title align-right">{{ $t('summary.prAmount') }}: </label>
                  <label class="currency-amount align-right">
                    {{ prAmount }}
                  </label>
                </div>
              </div>

              <div
                v-show="showAttachments"
                id="attachments"
                class="align-left summary-attachment"
              >
                <div class="title">
                  {{ $t('attachments') }}:
                </div>
                <FileUpload
                  class="fileUpload"
                  :invalidDescriptionLength="invalidDescriptionLength"
                  :attachment.sync="attachment"
                  :resourceId="analysisReco.id"
                  :readonly="isReadOnly"
                />
              </div>
            </div>
          </div>
        </template>
      </Collapse>
    </div>

    <AttachedPo
      v-show="isAwarded"
      class="recommendation-container"
      :numberType="numberType"
      :purchaseNumbers="numbers"
      :poContracts="poContracts"
      :resourceIdMap="poResourceIdMap"
      :awardedDate="awardedDate"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import Collapse from '@/controls/Collapse'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Sticky from '@/controls/Sticky'
import FileUpload from '@/modules/FileUpload'
import AttachedPo from '@/modules/projects/AttachedPo'
import AwardTender from '@/modules/tenders/review/AwardTender'
import Attachments from '@/modules/tenders/view/Attachments'
import CurrencyFormatter from 'currency-formatter'

import { updateDescription } from '@/api/docs/request'
import * as printer from '@/api/printers/request'
import { getPurchases } from '@/api/tenders/util'
import checkEditableMixin from '@/modules/tenders/checkEditableMixin'
import keepEditStatusMixin from '@/modules/tenders/keepEditStatusMixin'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import note from '@/plugin/VNoteDirective'
import dateUtil from '@/utils/dateUtil'
import { bidStatusMap, tenderStatus, vendorBidStatus } from '@/utils/locale'
import notification from '@/utils/notification'
import numberUtils from '@/utils/numberUtils'

export default {
  directives: { note },
  components: {
    Attachments,
    AttachedPo,
    AwardTender,
    Collapse,
    FileUpload,
    HKLSelect,
    Sticky,
  },
  mixins: [checkEditableMixin, keepEditStatusMixin],
  data () {
    const options = []

    return {
      options,
      componentName: '',
      attachment: {
        description: '',
        files: [],
      },
      tenderId: null,
      lowestPrice: 0,
      isEditing: false,
      invalidDescriptionLength: false,
      selectedBidVendorName: '',
      poFiles: [],
      deep,
      pdfId: null,
      CurrencyFormatter,
    }
  },
  methods: {
    ...mapActions('tenders', [
      'refreshTender',
      'saveAnalysisReco',
      'submitBid',
    ]),
    ...mapMutations('tenders', {
      updateTenderByPath: 'TENDER_UPDATE_PATH_FIELD',
    }),

    highlightSelectedBid (rec) {
      return rec.status === vendorBidStatus.SUBMITTED && rec.id === this.selectedBidId
    },

    getBidTotal (bid) {
      const totalFeeAdjusted = deep(bid, 'content.service.totalFeeAdjusted')
      if (totalFeeAdjusted !== undefined && totalFeeAdjusted !== null && totalFeeAdjusted !== '') {
        return totalFeeAdjusted
      }
      return deep(bid, 'content.service.totalFee')
    },

    highlightLowestBid (rec) {
      return rec.status === vendorBidStatus.SUBMITTED && rec.content && rec.content.service &&
        (this.getBidTotal(rec) === this.lowestPrice)
    },

    showDiffToLowestAmount (rec) {
      return (![bidStatusMap.NO_SHOW, bidStatusMap.DECLINED].includes(bidStatusMap[rec.status])) && rec.content && rec.content.service
        ? `${this.currencyCode} ` + CurrencyFormatter.format((this.getBidTotal(rec) || 0) - this.lowestPrice, 'USD') : ''
    },
    showPctToLowestSum (rec) {
      return (![bidStatusMap.NO_SHOW, bidStatusMap.DECLINED].includes(bidStatusMap[rec.status])) && rec.content && rec.content.service
        ? (((this.getBidTotal(rec) || 0) - this.lowestPrice) / (this.lowestPrice || 1) * 100).toFixed(2) + '%' : ''
    },
    showAdjustedInput (rec) {
      const totalFee = deep(rec, 'content.service.totalFee')
      return (![bidStatusMap.NO_SHOW, bidStatusMap.DECLINED].includes(bidStatusMap[rec.status])) && rec.content && rec.content.service && totalFee >= 0
    },

    updateAnalysisData (fieldName, newValue) {
      const path = ['general', 'analysis', 'data', fieldName]
      this.updateTenderByPath({ path, newValue })
    },
    markUpdate (bid, value) {
      for (let i = 0; i < this.currentTender.bids.length; i += 1) {
        const vendorBid = this.currentTender.bids[i]
        if (vendorBid.id === bid.id) {
          this.updateTotalFeeAdjustedText(i, value)
          const path = ['bids', i, 'content', 'service', 'totalFeeAdjusted']
          const totalFeeAdjusted = Number(numberUtils.formatDecimal(String(value)))
          this.updateTenderByPath({ path, newValue: totalFeeAdjusted, remove: !value })
          this.updateTenderByPath({ path: ['bids', i, 'updated'], newValue: true })
          continue
        }
      }
      this.refreshLowestPrice(this.bids)
    },
    updateTotalFeeAdjustedText (index, newValue) {
      const path = ['bids', index, 'content', 'service', 'totalFeeAdjustedText']
      this.updateTenderByPath({ path, newValue })
    },
    setTotalFeeAdjustedText (bids = []) {
      for (let i = 0; i < bids.length; i += 1) {
        const bid = bids[i]
        if (bid.content && bid.content.service && bid.content.service.totalFeeAdjusted &&
          !bid.content.service.totalFeeAdjustedText) {
          this.updateTotalFeeAdjustedText(i, bid.content.service.totalFeeAdjusted)
        }
      }
    },
    refreshLowestPrice (bids = []) {
      let lowestPrice = Number.MAX_SAFE_INTEGER
      lowestPrice = bids.reduce((curLowestPrice, bid) => {
        if (bidStatusMap[bid.status] === bidStatusMap.SUBMITTED &&
          bid.content && bid.content.service && (bid.content.service.totalFee >= 0)) {
          const bidTotalFee = this.getBidTotal(bid)
          if (curLowestPrice > bidTotalFee) {
            curLowestPrice = bidTotalFee; // eslint-disable-line
          }
        }
        return curLowestPrice
      }, lowestPrice)
      this.lowestPrice = lowestPrice
      setTimeout(() => {
        fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'})
      }, 0)
    },
    setRecommendedBidId (selectedBidVendorName) {
      this.selectedBidVendorName = selectedBidVendorName
      const path = ['general', 'analysis', 'data', 'recommendedBidId']
      this.updateTenderByPath({ path, newValue: this.recommendedBids[selectedBidVendorName].id })
    },
    async saveAttachment () {
      this.invalidDescriptionLength = false
      await Promise.all(
        this.attachment.files.map(async (file) => {
          if (file.description) {
            file.description = file.description.trim()
            if (file.description.length > 255) {
              this.invalidDescriptionLength = true
              return file
            }
            await updateDescription(file)
          }
          return file
        }),
      )
      if (this.invalidDescriptionLength === true) {
        return false
      }
      return true
    },

    getResponseData (response) {
      if (!response) {
        return ''
      }
      const data = response.data.data
      return data
    },

    async populateRecoBid () {
      if (this.analysisReco.data.recommendedBidId) {
        this.options.forEach(bid => { // eslint-disable-line
          if (bid.content.id === this.analysisReco.data.recommendedBidId) {
            this.selectedBid = bid
          }
        })
      }
    },
    async enableEdit () {
      await this.checkEditable(this.tenderId)
      if (this.editable) {
        this.isEditing = true
        this.keepEdit(this.tenderId, () => { this.isEditing = false })
      }
    },
    async editableCheck () {
      await this.checkEditable(this.tenderId)
      return this.editable
    },
    async cancelEdit () {
      this.isEditing = false
      this.clearEditingStatus()
      await this.refreshTender()
    },
    async save () {
      fusionInputCheckUtil.validateAllFields()
      if (!fusionInputCheckUtil.isAllFieldsValid('#analysis-recos-content')) {
        notification.fireNotification(false, this.$t('hint.checkValid'))
        return
      }
      try {
        // Update adjusted total fee
        await Promise.all(
          this.bids.map(async (bid) => {
            if (bid.updated) {
              const response = await this.submitBid({
                bidId: bid.id,
                request: { content: bid.content },
              })
              return response
            }
            return null
          }),
        )
        // Update recommendation info
        await this.saveAnalysisReco()
        // Save attachments
        const attachmentSaveResult = await this.saveAttachment()
        if (!attachmentSaveResult) {
          notification.fireNotification(false, this.$t('summary.saveAnalysisErrorMsg'))
          return
        }
        this.isEditing = false
        this.clearEditingStatus()
        formUtils.resetDirty()
        await this.refreshTender()
        notification.fireNotification(true, this.$t('summary.saveAnalysisMsg'))
      } catch (err) {
        notification.handleResponseError(err)
      }
    },

    getRouterLink (name) {
      const routerLink = this.$router.resolve({
        name,
        params: { id: this.tenderId },
      })

      return routerLink.href
    },
    openAnalysis () {
      const routeName = this.isEngineer ? 'tenderComparison' : 'tenderComparisonExt'
      window.open(this.getRouterLink(routeName), '_blank').focus()
    },
    async exportPDF () {
      if (this.canEdit && this.isEditing) {
        await this.save()
      }

      const bids = []
      this.bids.forEach(bid => {
        const reco = {
          name: bid.company.name,
          recoAmount: this.renderedStatus(bid),
        }
        if (bidStatusMap[bid.status] !== bidStatusMap.NO_SHOW && bidStatusMap[bid.status] !== bidStatusMap.DECLINED) {
          reco.highlight = this.selectedBidVendorName === reco.name
          reco.diffToLowestAmount = this.showDiffToLowestAmount(bid)
          reco.pctToLowestAmount = this.showPctToLowestSum(bid)
          const finalAmount = this.getBidTotal(bid)
          reco.finalAmount = `${this.currencyCode} ` + CurrencyFormatter.format(finalAmount, 'USD')
        }
        bids.push(reco)
      })
      this.pdfId = await printer.exportTenderSummary({
        prNumber: this.currentTender.general.prNumber,
        prDescription: this.currentTender.general.prDescription,
        site: lodash.get(this.currentTender, 'purchaseRequisition.siteId') || this.currentTender.general.site || '',
        prAmount: this.prAmount,
        finalAmount: this.recommendedAmount || '--',
        recommendedName: this.selectedBidVendorName || '',
        recommendation: this.analysisReco.data.recommendation || '',
        analysis: this.analysisReco.data.analysis,
        summary: this.analysisReco.data.summary,
        bids,
        currency: this.currency,
        link: window.location.href,
      })
    },

    getSortedBids (bids = []) {
      let submittedBids = []
      let declinedBids = []
      let noShowBids = []
      let others = []
      bids.forEach((bid) => {
        if (bidStatusMap[bid.status] === bidStatusMap.SUBMITTED) {
          submittedBids.push(bid)
        } else if (bidStatusMap[bid.status] === bidStatusMap.DECLINED) {
          declinedBids.push(bid)
        } else if (bidStatusMap[bid.status] === bidStatusMap.NO_SHOW) {
          noShowBids.push(bid)
        } else {
          others.push(bid)
        }
      })
      const orderedSubmittedBids = lodash.orderBy(submittedBids, (bid) => {
        const val = (bid.content && bid.content.service ? this.getBidTotal(bid)
          : Number.MAX_VALUE)
        return val
      })
      const orderedDeclinedBids = lodash.orderBy(declinedBids, (bid) => {
        return bid.name
      })
      const orderedNoShowBids = lodash.orderBy(noShowBids, (bid) => {
        return bid.name
      })
      return [...orderedSubmittedBids, ...orderedDeclinedBids, ...orderedNoShowBids, ...others]
    },

    renderedStatus (bid) {
      return bid.status === vendorBidStatus.SUBMITTED && bid.content && bid.content.service
        ? `${this.currencyCode} ` + CurrencyFormatter.format(bid.content.service.totalFee, 'USD')
        : bidStatusMap[bid.status]
    },
  },
  watch: {
    currentTender (newValue) {
      const tenderId = newValue.general.id
      if (!tenderId) return
      this.checkEditable(tenderId)
    },
  },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'isRepresentative']),
    ...mapGetters('user', ['currentUser', 'isEngineer']),
    numbers () {
      return deep(this.currentTender, 'general.numbers')
    },
    numberType () {
      return deep(this.currentTender, 'general.numberType')
    },
    poContracts () {
      return getPurchases(this.currentTender.general)
    },
    awardedDate () {
      const date = deep(this.currentTender, 'general.awardedDate')
      return date ? dateUtil.format(date, 'DD MMM YY HH:mm') : null
    },
    prAmount () {
      return this.currentTender.purchaseRequisition ? `${this.currencyCode} ` + CurrencyFormatter.format(this.currentTender.purchaseRequisition.budget, 'USD') : '--'
    },
    currencyCode () {
      return lodash.get(this.currentTender, 'purchaseRequisition.currencyCode')
    },
    isAwarded () {
      return [tenderStatus.AWARDED, tenderStatus.REGRETTED]
        .indexOf((this.currentTender.general || {}).status) > -1
    },
    isPicView () {
      return this.currentUser.id === this.currentTender.general.managedBy
    },
    isReview () {
      return deep(this.currentTender, 'general.status') === tenderStatus.REVIEW
    },
    canEdit () {
      return !this.isAwarded && (this.isPicView || this.isRepresentative)
    },
    isReadOnly () {
      return !this.canEdit || !this.isEditing
    },
    noteText () {
      return !this.editable ? this.$t('tender.underEdit', {editor: this.underEditBy}) : ''
    },
    showAttachments () {
      return this.canEdit || this.attachment.files.length > 0
    },
    analysisReco () {
      let analysisReco = this.currentTender.general.analysis || {}
      if (!analysisReco.data) {
        analysisReco.data = {}
        this.updateTenderByPath({
          path: ['general', 'analysis'],
          newValue: analysisReco,
        })
      }
      return analysisReco
    },
    poResourceIdMap () {
      if (this.analysisReco &&
        this.analysisReco.data &&
        this.analysisReco.data.poResourceIds) {
        return this.analysisReco.data.poResourceIds
      }
      if (!Array.isArray(this.numbers) && this.analysisReco.data.poResourceId) {
        let result = {}
        result[this.numbers] = this.analysisReco.data.poResourceId
        return result
      }
      return {}
    },
    selectedBidId () {
      return this.analysisReco.data.recommendedBidId
    },
    recommendedBids () {
      let recommendedBids = {}
      this.bids.forEach(bid => {
        if (bid.status === vendorBidStatus.SUBMITTED) {
          recommendedBids[bid.company.name] = bid
          if (this.selectedBidId && bid.id === this.selectedBidId) {
            this.selectedBidVendorName = bid.company.name
          }
        }
      })
      return recommendedBids
    },
    recommendedVendorHklOptions () {
      return Object.keys(this.recommendedBids).map((value) => ({
        name: value,
        value,
      }))
    },
    recommendedAmount () {
      const emptyValue = '--'
      if (!this.selectedBidVendorName) {
        return emptyValue
      }
      const selectedBid = this.recommendedBids[this.selectedBidVendorName]
      const service = deep(selectedBid, 'content.service')
      return service ? `${this.currencyCode} ` + CurrencyFormatter.format(service.totalFeeAdjusted || service.totalFee, 'USD') : emptyValue
    },
    bids () {
      const bids = this.currentTender.bids
      this.setTotalFeeAdjustedText(bids)
      this.refreshLowestPrice(bids)
      return this.getSortedBids(bids)
    },
    showQuerySummary () {
      if (!this.bids || this.bids.length === 0) return false
      return this.bids.some(bid => bid.queries && bid.queries.length > 0)
    },
  },
  async mounted () {
    this.tenderId = this.$router.currentRoute.params.id
    await this.checkEditable(this.tenderId)
  },
  destroyed () {
    this.clearEditingStatus()
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/table';

#accordion-recommendation-content {
  padding-top: 20px;
  padding-bottom: 20px;
}
.open-sum {
  min-width: 170px;
}
.lowestLine {
  &, input[type='number'] {
    color: #7E9943;
  }
}
.input {
  min-width: 80px
}
.sticky {
  height:60px
}
.fileUpload{
  border: solid 1px #e0e0e0;
  margin-bottom: 1.5rem;
}
.title {
  font-family: Verdana;
  font-size: 16px;
  text-align: left;
  color: #0c3a58;
  padding-bottom: .5rem;
}

.description {
  text-align: left;
  color: #9da7bd;
  padding-bottom: .5rem;
  textarea {
    width: 100%;
  }
}

.row {
  width: 100%;
  max-width: 100%;
}

.columns {
  padding: 0;
}

.recommendation-container {
  background-color: #f9f9f9;
  textarea {
    border-color: #e0e0e0;
  }
}

.summary-attachment {
  margin-top: 1rem;
}

.currency-amount {
  font-family: Verdana;
  font-size: 16px;
  text-align: left;
  color: #0c3a58;
}

input {
  margin: 0;
  display:inline-block;
  width: 90%;
}

.nav-bar {
  display: block;
  background: white;
}

.nav-bar a, .nav-bar button {
  float: right;
  margin-left: 15px;
}

.nav-bar-item {
  float: right;
  padding-left: 15px;
}

.align-right {
  text-align: right;
}
.align-center {
  text-align: center;
}

.btn {
  margin-bottom: 0;
}

.highlight-recommended {
  border: solid #e69500 2px;
}

.add-dollar-icon {
  input[type="text"] {
    height: 24px;
    padding: 0 8px;
  }
  &:before {
    content: '\0024\00a0';
  }
}

#recommend-vendor .multiselect {
  width: 95%;
  margin-bottom: 5px;
  padding-right: 0;
}

.pre-text {
  padding: 10px 10px;
  color: #0C3A58;
  border: solid 1px #e0e0e0;
}

ul {
  margin-left: 0;
}
li {
  list-style-type: none;
}

.final-amount {
  min-width: 200px;
}
</style>
